// NFL team primary colors for the draft board grid cell backgrounds.
// Each entry: bg (cell background), fg (text color picked for AA contrast
// on the bg). Sleeper player data uses these abbreviations directly (the
// `team` field on /v1/players/nfl entries).
//
// Source: official team brand guidelines, cross-referenced against
// https://teamcolorcodes.com/nfl-team-color-codes/
//
// Note: some primaries look muddy in a small cell (Eagles "Midnight Green"
// #004C54 reads as dark teal; Saints "Old Gold" #D3BC8D reads as tan).
// These ARE the official brand colors — switching to brighter, more
// recognizable alternatives would mean leaving the brand palette, which
// the spec explicitly asked us not to do. The visual punch comes from
// having 32 distinct cells per the team palette, not from any one cell.
//
// Default used for rookies w/ no NFL team yet (and as a catch-all).

const TEAM_COLORS = {
  ARI: { bg: "#97233F", fg: "#FFFFFF" }, // Arizona Cardinals
  ATL: { bg: "#A71930", fg: "#FFFFFF" }, // Atlanta Falcons
  BAL: { bg: "#241773", fg: "#FFFFFF" }, // Baltimore Ravens
  BUF: { bg: "#00338D", fg: "#FFFFFF" }, // Buffalo Bills
  CAR: { bg: "#0085CA", fg: "#FFFFFF" }, // Carolina Panthers
  CHI: { bg: "#0B162A", fg: "#FFFFFF" }, // Chicago Bears
  CIN: { bg: "#FB4F14", fg: "#FFFFFF" }, // Cincinnati Bengals
  CLE: { bg: "#311D00", fg: "#FFFFFF" }, // Cleveland Browns
  DAL: { bg: "#003594", fg: "#FFFFFF" }, // Dallas Cowboys
  DEN: { bg: "#FB4F14", fg: "#FFFFFF" }, // Denver Broncos
  DET: { bg: "#0076B6", fg: "#FFFFFF" }, // Detroit Lions
  GB:  { bg: "#203731", fg: "#FFB612" }, // Green Bay Packers
  HOU: { bg: "#03202F", fg: "#FFFFFF" }, // Houston Texans
  IND: { bg: "#002C5F", fg: "#FFFFFF" }, // Indianapolis Colts
  JAX: { bg: "#101820", fg: "#D7A22A" }, // Jacksonville Jaguars
  KC:  { bg: "#E31837", fg: "#FFFFFF" }, // Kansas City Chiefs
  LAC: { bg: "#0080C6", fg: "#FFFFFF" }, // Los Angeles Chargers
  LAR: { bg: "#003594", fg: "#FFD100" }, // Los Angeles Rams
  LV:  { bg: "#000000", fg: "#A5ACAF" }, // Las Vegas Raiders
  MIA: { bg: "#008E97", fg: "#FFFFFF" }, // Miami Dolphins
  MIN: { bg: "#4F2683", fg: "#FFC62F" }, // Minnesota Vikings
  NE:  { bg: "#002244", fg: "#FFFFFF" }, // New England Patriots
  NO:  { bg: "#D3BC8D", fg: "#101820" }, // New Orleans Saints
  NYG: { bg: "#0B2265", fg: "#FFFFFF" }, // New York Giants
  NYJ: { bg: "#125740", fg: "#FFFFFF" }, // New York Jets
  PHI: { bg: "#004C54", fg: "#FFFFFF" }, // Philadelphia Eagles
  PIT: { bg: "#101820", fg: "#FFB612" }, // Pittsburgh Steelers
  SEA: { bg: "#002244", fg: "#69BE28" }, // Seattle Seahawks
  SF:  { bg: "#AA0000", fg: "#FFFFFF" }, // San Francisco 49ers
  TB:  { bg: "#D50A0A", fg: "#FFFFFF" }, // Tampa Bay Buccaneers
  TEN: { bg: "#0C2340", fg: "#4B92DB" }, // Tennessee Titans
  WAS: { bg: "#5A1414", fg: "#FFB612" }, // Washington Commanders

  // Sleeper sometimes returns alternates / legacy codes — alias them.
  WSH: { bg: "#5A1414", fg: "#FFB612" }, // alias for Washington
  OAK: { bg: "#000000", fg: "#A5ACAF" }, // legacy Oakland Raiders
  SD:  { bg: "#0080C6", fg: "#FFFFFF" }, // legacy San Diego Chargers
  STL: { bg: "#003594", fg: "#FFD100" }, // legacy St. Louis Rams

  // #22 historical-team data (nflverse rosters, 2005+) surfaces additional
  // legacy/relocation codes and nflverse's GSIS-style alt spellings. Aliased
  // to their canonical franchise colors so old-season chips aren't gray.
  SL:  { bg: "#003594", fg: "#FFD100" }, // legacy St. Louis Rams (nflverse code)
  LA:  { bg: "#003594", fg: "#FFD100" }, // Los Angeles Rams (nflverse uses LA, LAC=Chargers)
  ARZ: { bg: "#97233F", fg: "#FFFFFF" }, // Arizona Cardinals (alt spelling)
  BLT: { bg: "#241773", fg: "#FFFFFF" }, // Baltimore Ravens (alt spelling)
  CLV: { bg: "#311D00", fg: "#FFFFFF" }, // Cleveland Browns (alt spelling)
  HST: { bg: "#03202F", fg: "#FFFFFF" }, // Houston Texans (alt spelling)
};

// Fallback for rookies with no NFL team yet, retired players, or any
// abbreviation not in the map above. Picked to look reasonable in a grid
// of bright team colors — neutral dark slate.
export const DEFAULT_TEAM_COLOR = { bg: "#374151", fg: "#E5E7EB" };

export function teamColor(abbr) {
  if (!abbr) return DEFAULT_TEAM_COLOR;
  return TEAM_COLORS[abbr.toUpperCase()] || DEFAULT_TEAM_COLOR;
}

export default TEAM_COLORS;
